import { useState } from 'react';
import { Copy, Check, ArrowLeft } from 'lucide-react';
import { Link } from 'react-router-dom';

function CodeBlock({ code }: { code: string }) {
  const [copied, setCopied] = useState(false);
  const handleCopy = () => {
    navigator.clipboard.writeText(code);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  return (
    <div className="relative group my-4">
      <button
        onClick={handleCopy}
        className="absolute top-2 right-2 p-1.5 rounded-md bg-white/10 hover:bg-white/20 text-gray-400 hover:text-white opacity-0 group-hover:opacity-100 transition-all"
      >
        {copied ? <Check className="w-4 h-4 text-green-400" /> : <Copy className="w-4 h-4" />}
      </button>
      <pre className="bg-black/60 border border-white/10 rounded-lg p-4 overflow-x-auto text-sm">
        <code className="text-gray-300 font-mono">{code}</code>
      </pre>
    </div>
  );
}

function Table({ headers, rows }: { headers: string[]; rows: string[][] }) {
  return (
    <div className="overflow-x-auto my-4">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-white/10">
            {headers.map((h, i) => (
              <th key={i} className="text-left py-3 px-4 text-gray-400 font-medium">{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => (
            <tr key={i} className="border-b border-white/5 hover:bg-white/[0.02]">
              {row.map((cell, j) => (
                <td key={j} className="py-3 px-4 text-gray-300">
                  {cell.startsWith('`') ? <code className="text-blue-400 font-mono text-xs">{cell.replace(/`/g, '')}</code> : cell}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Note({ children }: { children: React.ReactNode }) {
  return (
    <div className="my-4 p-4 rounded-lg bg-blue-500/10 border border-blue-500/20 text-blue-300 text-sm">
      {children}
    </div>
  );
}

export default function ApiDocs() {
  return (
    <div className="min-h-screen bg-[#0a0a0f] text-gray-200">
      {/* Header */}
      <div className="sticky top-0 z-10 bg-[#0a0a0f]/90 backdrop-blur-md border-b border-white/10">
        <div className="max-w-5xl mx-auto px-6 py-4 flex items-center gap-4">
          <Link to="/" className="text-gray-400 hover:text-white transition-colors">
            <ArrowLeft className="w-5 h-5" />
          </Link>
          <h1 className="text-lg font-bold text-white">API 文档</h1>
          <span className="text-xs text-gray-500 bg-white/5 px-2 py-0.5 rounded">v2.2.0</span>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-5xl mx-auto px-6 py-12 space-y-12">
        {/* 1. 概述 */}
        <section>
          <h2 className="text-2xl font-bold text-white mb-4">1. 概述</h2>
          <p className="text-gray-400 leading-relaxed">
            MYAIGC 是一个 AI 语音合成平台，提供文本转语音（TTS）、声音克隆、语音转录（ASR）等服务。本文档描述了所有可用的 API 接口，供开发者集成使用。
          </p>
          <p className="mt-2 text-gray-400">
            <strong className="text-gray-300">Base URL</strong>: <code className="text-blue-400 font-mono bg-white/5 px-1.5 py-0.5 rounded">https://aivoice.yandongli.eu.org</code>
          </p>
        </section>

        <hr className="border-white/10" />

        {/* 2. 认证方式 */}
        <section>
          <h2 className="text-2xl font-bold text-white mb-4">2. 认证方式</h2>
          <p className="text-gray-400 mb-6">平台支持两种认证方式，任选其一即可：</p>

          <h3 className="text-lg font-semibold text-white mb-3">2.1 API Key 认证</h3>
          <p className="text-gray-400 mb-2">通过 <code className="text-blue-400 font-mono text-sm">X-API-Key</code> 请求头传递 API Key。</p>
          <CodeBlock code="X-API-Key: your_api_key_here" />
          <p className="text-gray-500 text-sm">获取方式：登录后端或通过 POST /auth/login 接口获取。</p>

          <h3 className="text-lg font-semibold text-white mt-6 mb-3">2.2 Access Token 认证（推荐）</h3>
          <p className="text-gray-400 mb-2">通过 <code className="text-blue-400 font-mono text-sm">Authorization: Bearer</code> 请求头传递 Access Token。</p>
          <CodeBlock code="Authorization: Bearer your_access_token_here" />
          <p className="text-gray-500 text-sm">获取方式：登录后在个人中心创建 Access Token。</p>

          <h3 className="text-lg font-semibold text-white mt-6 mb-3">2.3 认证优先级</h3>
          <p className="text-gray-400">当同时提供两种认证方式时，<code className="text-blue-400 font-mono text-sm">X-API-Key</code> 优先级高于 <code className="text-blue-400 font-mono text-sm">Authorization: Bearer</code>。</p>

          <h3 className="text-lg font-semibold text-white mt-6 mb-3">2.4 两种认证方式的区别</h3>
          <Table
            headers={['特性', 'API Key', 'Access Token']}
            rows={[
              ['获取方式', '登录时自动生成', '用户手动创建'],
              ['数量', '每用户 1 个', '每用户最多 20 个'],
              ['权限', '与用户一致', '与用户一致'],
              ['适用场景', '前端登录态', 'API 接口调用、第三方集成'],
              ['吊销', '不可单独吊销', '可随时吊销'],
            ]}
          />
        </section>

        <hr className="border-white/10" />

        {/* 3. 通用说明 */}
        <section>
          <h2 className="text-2xl font-bold text-white mb-4">3. 通用说明</h2>

          <h3 className="text-lg font-semibold text-white mb-3">3.1 请求格式</h3>
          <ul className="list-disc list-inside text-gray-400 space-y-1 mb-6">
            <li>JSON 接口：<code className="text-blue-400 font-mono text-sm">Content-Type: application/json</code></li>
            <li>文件上传接口：<code className="text-blue-400 font-mono text-sm">Content-Type: multipart/form-data</code></li>
          </ul>

          <h3 className="text-lg font-semibold text-white mb-3">3.2 响应格式</h3>
          <p className="text-gray-400 mb-2">成功响应通常为 JSON 格式：</p>
          <CodeBlock code={`{\n  "success": true,\n  "data": {}\n}`} />
          <p className="text-gray-400 mt-2">TTS 接口成功时返回 <code className="text-blue-400 font-mono text-sm">audio/wav</code> 二进制音频流。</p>

          <h3 className="text-lg font-semibold text-white mt-6 mb-3">3.3 错误码</h3>
          <Table
            headers={['HTTP 状态码', '说明']}
            rows={[
              ['400', '请求参数错误'],
              ['401', '未认证或认证失败'],
              ['403', '权限不足（非管理员访问管理员接口）'],
              ['404', '资源不存在'],
              ['429', '每日配额已用完'],
              ['500', '服务器内部错误'],
              ['503', '服务暂不可用（推理队列已满）'],
            ]}
          />

          <h3 className="text-lg font-semibold text-white mt-6 mb-3">3.4 配额说明</h3>
          <ul className="list-disc list-inside text-gray-400 space-y-1">
            <li>每个用户有每日调用配额</li>
            <li>每次 TTS 语音合成消耗 1 次配额</li>
            <li>每次 ASR 语音转录消耗 1 次配额</li>
            <li>配额每天 00:00 自动重置</li>
            <li>管理员配额为 999999</li>
          </ul>
        </section>

        <hr className="border-white/10" />

        {/* 4. TTS 接口 */}
        <section>
          <h2 className="text-2xl font-bold text-white mb-4">4. 语音合成接口 (TTS)</h2>
          <p className="text-gray-400 mb-6">所有 TTS 接口成功时返回 <code className="text-blue-400 font-mono text-sm">audio/wav</code> 格式的二进制音频流。</p>

          {/* voice-design */}
          <div className="bg-white/[0.02] border border-white/10 rounded-xl p-6 mb-6">
            <div className="flex items-center gap-3 mb-4">
              <span className="px-2.5 py-1 rounded-md bg-green-500/20 text-green-400 text-xs font-bold">POST</span>
              <code className="text-white font-mono text-lg">/tts/voice-design</code>
            </div>
            <p className="text-gray-300 mb-4">声音设计 — 通过文本描述创建声音。</p>
            <p className="text-gray-500 text-sm mb-3">认证：需要 &nbsp;|&nbsp; 请求格式：multipart/form-data</p>
            <Table
              headers={['参数', '类型', '必填', '默认值', '说明']}
              rows={[
                ['text', 'string (Form)', '是', '-', '要合成的文本'],
                ['voice_description', 'string (Form)', '是', '-', '声音描述（如"年轻女性的声音"）'],
                ['cfg_value', 'float (Form)', '否', '2.0', 'CFG 值，范围 1.0-3.0'],
                ['inference_timesteps', 'int (Form)', '否', '10', '推理步数，范围 1-50'],
              ]}
            />
            <p className="text-gray-500 text-sm mt-3">成功响应：audio/wav 文件流</p>
            <CodeBlock code={`curl -X POST "https://aivoice.yandongli.eu.org/tts/voice-design" \\\n  -H "Authorization: Bearer your_access_token" \\\n  -F "text=你好，欢迎使用语音合成服务" \\\n  -F "voice_description=温柔的女声" \\\n  -F "cfg_value=2.0" \\\n  -F "inference_timesteps=10" \\\n  --output output.wav`} />
          </div>

          {/* controllable-clone */}
          <div className="bg-white/[0.02] border border-white/10 rounded-xl p-6 mb-6">
            <div className="flex items-center gap-3 mb-4">
              <span className="px-2.5 py-1 rounded-md bg-green-500/20 text-green-400 text-xs font-bold">POST</span>
              <code className="text-white font-mono text-lg">/tts/controllable-clone</code>
            </div>
            <p className="text-gray-300 mb-4">可控克隆 — 通过参考音频和控制指令生成语音。</p>
            <p className="text-gray-500 text-sm mb-3">认证：需要 &nbsp;|&nbsp; 请求格式：multipart/form-data</p>
            <Table
              headers={['参数', '类型', '必填', '默认值', '说明']}
              rows={[
                ['text', 'string (Form)', '是', '-', '要合成的文本'],
                ['reference_audio', 'file (File)', '是', '-', '参考音频文件'],
                ['control_instruction', 'string (Form)', '否', '""', '控制指令'],
                ['cfg_value', 'float (Form)', '否', '2.0', 'CFG 值，范围 1.0-3.0'],
                ['inference_timesteps', 'int (Form)', '否', '10', '推理步数，范围 1-50'],
              ]}
            />
            <CodeBlock code={`curl -X POST "https://aivoice.yandongli.eu.org/tts/controllable-clone" \\\n  -H "Authorization: Bearer your_access_token" \\\n  -F "text=这是克隆的声音" \\\n  -F "reference_audio=@reference.wav" \\\n  -F "control_instruction=用悲伤的语气说话" \\\n  --output output.wav`} />
          </div>

          {/* ultimate-clone */}
          <div className="bg-white/[0.02] border border-white/10 rounded-xl p-6 mb-6">
            <div className="flex items-center gap-3 mb-4">
              <span className="px-2.5 py-1 rounded-md bg-green-500/20 text-green-400 text-xs font-bold">POST</span>
              <code className="text-white font-mono text-lg">/tts/ultimate-clone</code>
            </div>
            <p className="text-gray-300 mb-4">极致克隆 — 通过参考音频和提示文本实现高保真声音克隆。</p>
            <p className="text-gray-500 text-sm mb-3">认证：需要 &nbsp;|&nbsp; 请求格式：multipart/form-data</p>
            <Table
              headers={['参数', '类型', '必填', '默认值', '说明']}
              rows={[
                ['text', 'string (Form)', '是', '-', '要合成的文本'],
                ['reference_audio', 'file (File)', '是', '-', '参考音频文件'],
                ['prompt_text', 'string (Form)', '是', '-', '提示文本（参考音频对应的文字内容）'],
                ['cfg_value', 'float (Form)', '否', '2.0', 'CFG 值，范围 1.0-3.0'],
                ['inference_timesteps', 'int (Form)', '否', '10', '推理步数，范围 1-50'],
              ]}
            />
            <CodeBlock code={`curl -X POST "https://aivoice.yandongli.eu.org/tts/ultimate-clone" \\\n  -H "Authorization: Bearer your_access_token" \\\n  -F "text=这是极致克隆的声音" \\\n  -F "reference_audio=@reference.wav" \\\n  -F "prompt_text=这是参考音频中说话人说的原始内容" \\\n  --output output.wav`} />
          </div>

          {/* batch */}
          <div className="bg-white/[0.02] border border-white/10 rounded-xl p-6">
            <div className="flex items-center gap-3 mb-4">
              <span className="px-2.5 py-1 rounded-md bg-green-500/20 text-green-400 text-xs font-bold">POST</span>
              <code className="text-white font-mono text-lg">/tts/batch</code>
            </div>
            <p className="text-gray-300 mb-4">批量语音合成 — 一次最多合成 10 条文本。</p>
            <p className="text-gray-500 text-sm mb-3">认证：需要 &nbsp;|&nbsp; 请求格式：application/json</p>
            <CodeBlock code={`{\n  "texts": ["第一条文本", "第二条文本"],\n  "control_instruction": "string（可选）",\n  "cfg_value": 2.0,\n  "inference_timesteps": 10\n}`} />
            <Table
              headers={['字段', '类型', '必填', '约束', '说明']}
              rows={[
                ['texts', 'string[]', '是', '1-10 条', '要合成的文本列表'],
                ['control_instruction', 'string', '否', '最长 500 字符', '控制指令'],
                ['cfg_value', 'float', '否', '1.0-3.0', 'CFG 值'],
                ['inference_timesteps', 'int', '否', '1-50', '推理步数'],
              ]}
            />
          </div>
        </section>

        <hr className="border-white/10" />

        {/* 5. ASR 接口 */}
        <section>
          <h2 className="text-2xl font-bold text-white mb-4">5. 语音识别接口 (ASR)</h2>
          <div className="bg-white/[0.02] border border-white/10 rounded-xl p-6">
            <div className="flex items-center gap-3 mb-4">
              <span className="px-2.5 py-1 rounded-md bg-green-500/20 text-green-400 text-xs font-bold">POST</span>
              <code className="text-white font-mono text-lg">/asr/transcribe</code>
            </div>
            <p className="text-gray-300 mb-4">语音转录 — 将音频文件转换为文本。</p>
            <p className="text-gray-500 text-sm mb-3">认证：需要 &nbsp;|&nbsp; 请求格式：multipart/form-data</p>
            <Table
              headers={['参数', '类型', '必填', '说明']}
              rows={[['audio', 'file (File)', '是', '音频文件']]}
            />
            <CodeBlock code={`curl -X POST "https://aivoice.yandongli.eu.org/asr/transcribe" \\\n  -H "Authorization: Bearer your_access_token" \\\n  -F "audio=@speech.wav"`} />
          </div>
        </section>

        <hr className="border-white/10" />

        {/* 6. 认证接口 */}
        <section>
          <h2 className="text-2xl font-bold text-white mb-4">6. 认证接口 (Auth)</h2>

          <div className="bg-white/[0.02] border border-white/10 rounded-xl p-6 mb-6">
            <div className="flex items-center gap-3 mb-4">
              <span className="px-2.5 py-1 rounded-md bg-green-500/20 text-green-400 text-xs font-bold">POST</span>
              <code className="text-white font-mono text-lg">/auth/login</code>
            </div>
            <p className="text-gray-300 mb-4">用户登录，获取 API Key。</p>
            <CodeBlock code={`{\n  "username": "string",\n  "password": "string"\n}`} />
          </div>

          <div className="bg-white/[0.02] border border-white/10 rounded-xl p-6 mb-6">
            <div className="flex items-center gap-3 mb-4">
              <span className="px-2.5 py-1 rounded-md bg-blue-500/20 text-blue-400 text-xs font-bold">GET</span>
              <code className="text-white font-mono text-lg">/auth/profile</code>
            </div>
            <p className="text-gray-300 mb-4">获取当前用户信息。</p>
            <p className="text-gray-500 text-sm">认证：需要</p>
          </div>

          <div className="bg-white/[0.02] border border-white/10 rounded-xl p-6 mb-6">
            <div className="flex items-center gap-3 mb-4">
              <span className="px-2.5 py-1 rounded-md bg-green-500/20 text-green-400 text-xs font-bold">POST</span>
              <code className="text-white font-mono text-lg">/auth/register</code>
            </div>
            <p className="text-gray-300 mb-4">用户注册（发送邮箱验证码）。</p>
            <CodeBlock code={`{\n  "username": "string",\n  "email": "string",\n  "password": "string",\n  "invitation_code": "string（可选）"\n}`} />
          </div>

          <div className="bg-white/[0.02] border border-white/10 rounded-xl p-6 mb-6">
            <div className="flex items-center gap-3 mb-4">
              <span className="px-2.5 py-1 rounded-md bg-green-500/20 text-green-400 text-xs font-bold">POST</span>
              <code className="text-white font-mono text-lg">/auth/tokens</code>
            </div>
            <p className="text-gray-300 mb-4">创建新的 Access Token。</p>
            <p className="text-gray-500 text-sm mb-3">认证：需要</p>
            <CodeBlock code={`{\n  "name": "string"\n}`} />
            <Note>完整的 token 值仅在创建时返回一次，请立即保存。每用户最多创建 20 个 Token。</Note>
          </div>

          <div className="bg-white/[0.02] border border-white/10 rounded-xl p-6 mb-6">
            <div className="flex items-center gap-3 mb-4">
              <span className="px-2.5 py-1 rounded-md bg-blue-500/20 text-blue-400 text-xs font-bold">GET</span>
              <code className="text-white font-mono text-lg">/auth/tokens</code>
            </div>
            <p className="text-gray-300">列出当前用户的所有 Access Token（仅返回前 8 位前缀）。</p>
          </div>

          <div className="bg-white/[0.02] border border-white/10 rounded-xl p-6">
            <div className="flex items-center gap-3 mb-4">
              <span className="px-2.5 py-1 rounded-md bg-red-500/20 text-red-400 text-xs font-bold">DELETE</span>
              <code className="text-white font-mono text-lg">/auth/tokens/{'{token_id}'}</code>
            </div>
            <p className="text-gray-300">吊销指定的 Access Token。</p>
          </div>
        </section>

        <hr className="border-white/10" />

        {/* 7. VIP 兑换 */}
        <section>
          <h2 className="text-2xl font-bold text-white mb-4">7. VIP 兑换接口</h2>
          <div className="bg-white/[0.02] border border-white/10 rounded-xl p-6">
            <div className="flex items-center gap-3 mb-4">
              <span className="px-2.5 py-1 rounded-md bg-green-500/20 text-green-400 text-xs font-bold">POST</span>
              <code className="text-white font-mono text-lg">/auth/redeem-vip</code>
            </div>
            <p className="text-gray-300 mb-4">用户兑换 VIP 码，升级为 VIP 会员。</p>
            <p className="text-gray-500 text-sm mb-3">认证：需要</p>
            <CodeBlock code={`{\n  "code": "VIP-XXXXXXXXXXXX"\n}`} />
          </div>
        </section>

        <hr className="border-white/10" />

        {/* 8. 快速开始 */}
        <section>
          <h2 className="text-2xl font-bold text-white mb-4">8. 快速开始</h2>

          <h3 className="text-lg font-semibold text-white mb-3">步骤 1: 获取 Access Token</h3>
          <ol className="list-decimal list-inside text-gray-400 space-y-1 mb-6">
            <li>登录 MYAIGC 平台</li>
            <li>进入「个人中心」页面</li>
            <li>在「Access Tokens」区域点击「创建 Token」</li>
            <li>输入 Token 名称（如"我的应用"）</li>
            <li>复制生成的 Token 并妥善保存</li>
          </ol>

          <h3 className="text-lg font-semibold text-white mb-3">步骤 2: 调用 API</h3>
          <CodeBlock code={`# 声音设计\ncurl -X POST "https://aivoice.yandongli.eu.org/tts/voice-design" \\\n  -H "Authorization: Bearer your_access_token_here" \\\n  -F "text=你好世界" \\\n  -F "voice_description=温柔的女声" \\\n  --output hello.wav\n\n# 语音转录\ncurl -X POST "https://aivoice.yandongli.eu.org/asr/transcribe" \\\n  -H "Authorization: Bearer your_access_token_here" \\\n  -F "audio=@recording.wav"`} />

          <h3 className="text-lg font-semibold text-white mt-6 mb-3">Python 示例</h3>
          <CodeBlock code={`import requests\n\nAPI_BASE = "https://aivoice.yandongli.eu.org"\nACCESS_TOKEN = "your_access_token_here"\nHEADERS = {"Authorization": f"Bearer {ACCESS_TOKEN}"}\n\n# 声音设计\nresponse = requests.post(\n    f"{API_BASE}/tts/voice-design",\n    headers=HEADERS,\n    data={\n        "text": "你好世界",\n        "voice_description": "温柔的女声",\n        "cfg_value": 2.0,\n        "inference_timesteps": 10,\n    },\n)\nwith open("output.wav", "wb") as f:\n    f.write(response.content)\n\n# 语音转录\nwith open("recording.wav", "rb") as f:\n    response = requests.post(\n        f"{API_BASE}/asr/transcribe",\n        headers=HEADERS,\n        files={"audio": f},\n    )\nprint(response.json()["text"])`} />
        </section>

        {/* Footer */}
        <div className="pt-8 border-t border-white/10 text-center text-gray-600 text-sm">
          <p>&copy; {new Date().getFullYear()} MYAIGC. All rights reserved.</p>
        </div>
      </div>
    </div>
  );
}
